/**
 * Turn a report's stored money-affecting settings into the rows that disclose
 * "Ajustes que afectan al cálculo".
 *
 * ⚠️⚠️ VIVE EN `shared` PORQUE LO LEEN DOS SUPERFICIES. Estaba sólo en el
 * frontend, así que la PANTALLA declaraba los supuestos y el CSV descargado —el
 * artefacto que se archiva y se pasa al asesor— no llevaba ni uno. Duplicar la
 * lógica en el backend habría creado la divergencia que costó #1845 y #1848.
 *
 * This is the detail half of the disclosure: the history table marks that two
 * reports for a year are not alike, and this says what each one actually did.
 * Every value comes from `bat.tax_reports.configuration_snapshot` via
 * `/details`; nothing here re-resolves the live jurisdiction config, because a
 * report is a point-in-time artefact and today's rules are not the ones it ran
 * under.
 *
 * ⚠️ ABSENCE IS NOT `false`. A snapshot that never recorded a key produces no
 * row. "Comisiones de negociación: No deducidas" is a claim about a filing, and
 * a report that never stored the flag has not made it.
 */

import {
  type TaxReportMoneySettingKey,
  type TaxReportMoneySettings,
} from '../contracts/tax-reports/settings-disclosure.js'

/**
 * The subset rendered in this block.
 *
 * `costMethod` and `baseCurrency` are money-affecting and DO take part in the
 * history-table difference check, but they are deliberately not repeated here:
 * the same panel already prints them one line above as "Método" and "Moneda".
 * Duplicating them in a dense metadata grid buys nothing and pushes the six
 * settings nothing else discloses further down.
 */
export const MONEY_SETTINGS_SHOWN_IN_DETAIL_BLOCK = [
  'washSaleEnabled',
  'washSaleDays',
  'deductTradingFees',
  'deductGasFees',
  'deductWithdrawalFees',
  'deductMiningStakingExpenses',
] as const satisfies readonly TaxReportMoneySettingKey[]

/** i18n key prefix inside the backend-hosted `tax-reports` namespace. */
export const MONEY_SETTINGS_I18N_PREFIX = 'moneySettings'

/**
 * The subset of `useTranslation('tax-reports').t` this module needs.
 * Structural, so callers pass `t` straight through and tests pass a fake.
 */
export type TranslateMoneySetting = (i18nKey: string, options?: Record<string, unknown>) => string

export interface MoneySettingDisplayRow {
  key: TaxReportMoneySettingKey
  label: string
  value: string
  /**
   * El ALCANCE de una deducción activa. Presente sólo en las banderas de
   * comisión puestas a `true`: «Deducidas» a secas promete más de lo que el
   * motor hace, porque `sumFiatDeductibleFees` (cost-basis-engine.ts) sólo
   * resta las comisiones ya expresadas en la moneda del informe.
   *
   * Medido por la UI el 2026-08-20 con dos ventas de la misma importación:
   * comisión 0,67 EUR → ingresos 448,12 − 0,67 = 447,45 €; comisión
   * 0,00125 ETH → ingresos 3.412,80 € íntegros. Saltarse la comisión en cripto
   * es deliberado (convertirla exige un precio histórico y restar «unidades»
   * fabricaría cifras sin cota), pero no decirlo deja al usuario sin ninguna
   * superficie donde enterarse.
   */
  note?: string
}

/** The four fee-deduction flags, which share one value vocabulary. */
const FEE_DEDUCTION_KEYS = [
  'deductTradingFees',
  'deductGasFees',
  'deductWithdrawalFees',
  'deductMiningStakingExpenses',
] as const satisfies readonly TaxReportMoneySettingKey[]

function isFeeDeductionKey(key: TaxReportMoneySettingKey): boolean {
  return (FEE_DEDUCTION_KEYS as readonly TaxReportMoneySettingKey[]).includes(key)
}

/**
 * La nota de alcance. Nombra la moneda del informe cuando el snapshot la
 * recordó; si no, lo dice en genérico antes que inventarse una divisa — el
 * mismo criterio que rige todo este módulo: ausencia no es un valor.
 */
function buildFeeCurrencyNote(baseCurrency: string | undefined, t: TranslateMoneySetting): string {
  if (baseCurrency === undefined || baseCurrency.trim() === '') {
    return t(`${MONEY_SETTINGS_I18N_PREFIX}.notes.feeCurrencyLimitNoCurrency`)
  }
  return t(`${MONEY_SETTINGS_I18N_PREFIX}.notes.feeCurrencyLimit`, { currency: baseCurrency })
}

/**
 * Build the printable rows, in `MONEY_SETTINGS_SHOWN_IN_DETAIL_BLOCK` order.
 *
 * An empty result means the report recorded none of these settings, and the
 * caller must then render no block at all — an empty "Ajustes" heading would
 * read as "this report applied no rules".
 */
export function buildMoneySettingsDisplayRows(
  settings: TaxReportMoneySettings,
  t: TranslateMoneySetting,
): MoneySettingDisplayRow[] {
  const rows: MoneySettingDisplayRow[] = []

  for (const key of MONEY_SETTINGS_SHOWN_IN_DETAIL_BLOCK) {
    const label = t(`${MONEY_SETTINGS_I18N_PREFIX}.names.${key}`)

    if (key === 'washSaleEnabled') {
      const washSaleEnabled = settings.washSaleEnabled
      if (washSaleEnabled === undefined) continue
      rows.push({
        key,
        label,
        value: t(
          `${MONEY_SETTINGS_I18N_PREFIX}.values.${washSaleEnabled ? 'applied' : 'notApplied'}`,
        ),
      })
      continue
    }

    if (key === 'washSaleDays') {
      const washSaleDays = settings.washSaleDays
      if (washSaleDays === undefined) continue
      // The window only opened if the rule ran. The real ES snapshot stores
      // `washSaleDays: 30` with `washSaleEnabled: false`, so printing it
      // unconditionally would describe a look-back the engine never used.
      // `undefined` does NOT suppress it: not recording the rule is not
      // evidence that it was off.
      if (settings.washSaleEnabled === false) continue
      rows.push({
        key,
        label,
        value: t(`${MONEY_SETTINGS_I18N_PREFIX}.values.days`, { count: washSaleDays }),
      })
      continue
    }

    if (isFeeDeductionKey(key)) {
      const deducted = settings[key]
      if (typeof deducted !== 'boolean') continue
      rows.push({
        key,
        label,
        value: t(`${MONEY_SETTINGS_I18N_PREFIX}.values.${deducted ? 'deducted' : 'notDeducted'}`),
        // Sólo hay alcance que matizar cuando la deducción SE APLICÓ. Sobre un
        // «No deducidas» la nota sobraría y sonaría a excusa.
        ...(deducted ? { note: buildFeeCurrencyNote(settings.baseCurrency, t) } : {}),
      })
    }
  }

  return rows
}
